import {STATUS_OPTIONS, PRIORITY_OPTIONS, SLA_OPTIONS} from '../constants/filterOptions';

export const PERSISTED_FILTER_KEYS = [
	'status',
	'priority',
	'sla_status',
	'assignee_id',
	'label_id',
	'requester_id',
	'created_after',
	'created_before',
	'updated_after',
	'updated_before',
	'filter_source',
] as const;

type PersistedFilterKey = (typeof PERSISTED_FILTER_KEYS)[number];

const VALID_STATUS_VALUES = new Set(STATUS_OPTIONS.map(o => o.id));
const VALID_PRIORITY_VALUES = new Set(PRIORITY_OPTIONS.map(o => o.id));
const VALID_SLA_VALUES = new Set(SLA_OPTIONS.map(o => o.id));

const CLOSED_SET_VALIDATORS: Partial<Record<PersistedFilterKey, Set<string>>> = {
	status: VALID_STATUS_VALUES,
	priority: VALID_PRIORITY_VALUES,
	sla_status: VALID_SLA_VALUES,
};

const STORAGE_VERSION = 1;
const KEY_PREFIX = 'ticketing.ticketList.filters.v1.';

interface StoredBlob {
	v: number;
	params: Array<[string, string]>;
}

function storageKey(userId: string): string {
	return KEY_PREFIX + userId;
}

function isAllowedEntry(key: string, value: string): boolean {
	if ((PERSISTED_FILTER_KEYS as readonly string[]).includes(key)) {
		const validator = CLOSED_SET_VALIDATORS[key as PersistedFilterKey];
		if (validator) return validator.has(value);
		return typeof value === 'string';
	}
	if (key === 'view') return value === 'all';
	return false;
}

export function readStoredFilters(userId: string | undefined): URLSearchParams | null {
	if (!userId) return null;
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.localStorage.getItem(storageKey(userId));
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		if (
			!parsed ||
			typeof parsed !== 'object' ||
			(parsed as StoredBlob).v !== STORAGE_VERSION ||
			!Array.isArray((parsed as StoredBlob).params)
		) {
			return null;
		}
		const result = new URLSearchParams();
		for (const entry of (parsed as StoredBlob).params) {
			if (!Array.isArray(entry) || entry.length !== 2) continue;
			const [k, v] = entry;
			if (typeof k !== 'string' || typeof v !== 'string') continue;
			if (isAllowedEntry(k, v)) result.append(k, v);
		}
		return result;
	} catch {
		return null;
	}
}

export function writeStoredFilters(userId: string | undefined, params: URLSearchParams): void {
	if (!userId) return;
	if (typeof window === 'undefined') return;
	try {
		const entries: Array<[string, string]> = [];
		for (const [k, v] of params.entries()) {
			if (isAllowedEntry(k, v)) entries.push([k, v]);
		}
		const blob: StoredBlob = {v: STORAGE_VERSION, params: entries};
		window.localStorage.setItem(storageKey(userId), JSON.stringify(blob));
	} catch {
		// localStorage may be disabled or quota-exceeded; degrade silently.
	}
}
